import type { Session } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { getSession, onAuthStateChange } from '../auth';
import { toDataError, type DataError } from '../errors';

export interface SessionState {
  readonly session: Session | null;
  readonly loading: boolean;
  readonly error: DataError | null;
  /** true after a password-reset link signed the user in with a recovery session. */
  readonly recovery: boolean;
}

export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({ session: null, loading: true, error: null, recovery: false });

  useEffect(() => {
    let cancelled = false;

    getSession()
      .then((session) => {
        if (!cancelled) setState((prev) => ({ ...prev, session, loading: false, error: null }));
      })
      .catch((err: unknown) => {
        if (!cancelled) setState((prev) => ({ ...prev, loading: false, error: toDataError(err) }));
      });

    const unsubscribe = onAuthStateChange((event, session) => {
      if (cancelled) return;
      setState((prev) => ({
        session,
        loading: false,
        error: null,
        recovery: event === 'PASSWORD_RECOVERY' ? true : event === 'SIGNED_OUT' ? false : prev.recovery,
      }));
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return state;
}
